/**
 * <card-variant-picker>: variant dropdown on product cards.
 * The <select> is not submitted; its value is copied into the form's hidden
 * `id` input, which <product-form> posts. Price, unit price, image, quantity
 * rules and the button state follow the selected option's data attributes.
 */
if (!customElements.get('card-variant-picker')) {
  customElements.define(
    'card-variant-picker',
    class CardVariantPicker extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('[data-variant-select]');
        if (!this.select) return;

        this.card = this.closest('.card-wrapper');
        this.idInput = this.querySelector('input[name="id"]');
        this.qtyInput = this.querySelector('.quantity__input');
        this.button = this.querySelector('button[type="submit"]');
        this.buttonText = this.button?.querySelector('.card-vp__add-text');
        this.soldOutText = this.button?.querySelector('.sold-out-message');
        this.productForm = this.querySelector('product-form');

        this.select.addEventListener('change', () => this.update());
      }

      update() {
        const option = this.select.selectedOptions[0];
        if (!option) return;
        const data = option.dataset;
        const available = data.available === 'true';

        this.idInput.value = option.value;
        this.idInput.disabled = !available;

        this.updatePrice(data);
        this.updateImage(data.image);
        this.updateQuantity(data);
        this.updateButton(available);
      }

      updatePrice({ price, compare, unit }) {
        const block = this.card?.querySelector('[data-card-price]');
        if (!block) return;
        const now = block.querySelector('[data-price-now]');
        const was = block.querySelector('[data-price-was]');
        const wasValue = block.querySelector('[data-price-was-value]');
        const unitEl = block.querySelector('[data-price-unit]');

        if (now) now.textContent = price;
        if (was && wasValue) {
          wasValue.textContent = compare || '';
          was.hidden = !compare;
        }
        if (unitEl) {
          unitEl.textContent = unit || '';
          unitEl.hidden = !unit;
        }
      }

      updateImage(srcset) {
        if (!srcset) return;
        const img = this.card?.querySelector('.card__media img');
        if (!img) return;
        img.srcset = srcset;
        img.src = srcset.split(',')[1]?.trim().split(' ')[0] || img.src;
      }

      updateQuantity({ min, step, max }) {
        if (!this.qtyInput) return;
        const minimum = parseInt(min, 10) || 1;
        this.qtyInput.min = minimum;
        this.qtyInput.dataset.min = minimum;
        this.qtyInput.step = parseInt(step, 10) || 1;
        if (max) {
          this.qtyInput.max = max;
          this.qtyInput.dataset.max = max;
        } else {
          this.qtyInput.removeAttribute('max');
          delete this.qtyInput.dataset.max;
        }
        if (parseInt(this.qtyInput.value, 10) < minimum) this.qtyInput.value = minimum;
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Also clears the sold-out lock <product-form> sets after a failed add.
      updateButton(available) {
        if (!this.button) return;
        if (this.productForm) this.productForm.error = false;
        this.button.removeAttribute('aria-disabled');
        this.soldOutText?.classList.add('hidden');
        this.buttonText?.classList.remove('hidden');
        this.productForm?.handleErrorMessage?.();

        if (available) {
          this.button.removeAttribute('disabled');
          if (this.buttonText) this.buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.button.setAttribute('disabled', 'disabled');
          if (this.buttonText) this.buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }
    }
  );
}

/**
 * <collection-view-toggle>: grid / list switch for the collection grid.
 * The choice lives on <html data-collection-view> so it survives the product
 * grid being re-rendered by filtering and sorting, and in localStorage so it
 * survives page loads.
 */
if (!customElements.get('collection-view-toggle')) {
  customElements.define(
    'collection-view-toggle',
    class CollectionViewToggle extends HTMLElement {
      connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('[data-view]'));
        this.buttons.forEach((button) => button.addEventListener('click', () => this.setView(button.dataset.view)));
        this.sync();
      }

      setView(view) {
        document.documentElement.setAttribute('data-collection-view', view);
        try {
          localStorage.setItem('collection-view', view);
        } catch (e) {}
        // The toolbar and the mobile bar each have a toggle; keep both in step.
        document.querySelectorAll('collection-view-toggle').forEach((toggle) => toggle.sync());
      }

      sync() {
        const current = document.documentElement.getAttribute('data-collection-view') || 'grid';
        this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.view === current)));
      }
    }
  );
}
